import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { ArrowLeft, Calendar as CalendarIcon, Upload, FileSpreadsheet, Check, FileUp, X } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BulkImportPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState('');
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    gallery_name_prefix: '',
    event_type: '',
    event_date: null,
    csvData: ''
  });

  const handleFileRead = (file) => {
    if (!file) return;
    
    // Check file type
    const validTypes = ['text/csv', 'application/vnd.ms-excel', 'text/plain'];
    const isCSV = validTypes.includes(file.type) || file.name.endsWith('.csv');
    
    if (!isCSV) {
      toast.error('Please upload a CSV file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      setForm({ ...form, csvData: content });
      setFileName(file.name);
      toast.success(`Loaded ${file.name}`);
    };
    reader.onerror = () => {
      toast.error('Failed to read file');
    };
    reader.readAsText(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileRead(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileRead(files[0]);
    }
  };

  const clearFile = () => {
    setFileName('');
    setForm({ ...form, csvData: '' });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const parseCSV = (csvText) => {
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) return [];
    
    const headers = lines[0].toLowerCase().split(',').map(h => h.trim());
    const nameIndex = headers.findIndex(h => h.includes('name'));
    const emailIndex = headers.findIndex(h => h.includes('email'));
    
    if (nameIndex === -1 || emailIndex === -1) {
      toast.error('CSV must have "name" and "email" columns');
      return [];
    }
    
    const clients = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      if (values[nameIndex] && values[emailIndex]) {
        clients.push({
          name: values[nameIndex],
          email: values[emailIndex]
        });
      }
    }
    
    return clients;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.gallery_name_prefix || !form.event_type) {
      toast.error('Please fill in required fields');
      return;
    }
    
    const clients = parseCSV(form.csvData);
    if (clients.length === 0) {
      toast.error('No valid client data found in CSV');
      return;
    }
    
    setLoading(true);
    try {
      const data = {
        gallery_name_prefix: form.gallery_name_prefix,
        event_type: form.event_type,
        event_date: form.event_date ? format(form.event_date, 'yyyy-MM-dd') : null,
        clients
      };
      
      const response = await axios.post(`${API}/galleries/bulk-import`, data);
      setResult(response.data);
      toast.success(`Created ${response.data.galleries.length} galleries`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import galleries');
    } finally {
      setLoading(false);
    }
  };

  const sampleCSV = `name,email
John Smith,john@example.com
Jane Doe,jane@example.com
Mike Johnson,mike@example.com`;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/galleries')} data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900">Bulk Import</h1>
          <p className="text-slate-600">Create multiple galleries at once from a CSV file</p>
        </div>
      </div>

      {!result ? (
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-heading">Import Settings</CardTitle>
            <CardDescription>
              Upload a CSV file with client names and emails to create galleries for each client
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="prefix">Gallery Name Prefix *</Label>
                <Input
                  id="prefix"
                  placeholder="e.g., Soccer League 2024"
                  value={form.gallery_name_prefix}
                  onChange={(e) => setForm({ ...form, gallery_name_prefix: e.target.value })}
                  data-testid="prefix-input"
                  required
                />
                <p className="text-xs text-slate-500">
                  Each gallery will be named: "[Prefix] - [Client Name]"
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Event Type *</Label>
                  <Select 
                    value={form.event_type} 
                    onValueChange={(value) => setForm({ ...form, event_type: value })}
                  >
                    <SelectTrigger data-testid="event-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sports">Sports</SelectItem>
                      <SelectItem value="school">School</SelectItem>
                      <SelectItem value="portrait">Portrait</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Event Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                        data-testid="event-date-btn"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {form.event_date ? format(form.event_date, 'PPP') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={form.event_date}
                        onSelect={(date) => setForm({ ...form, event_date: date })}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Client Data (CSV file) *</Label>
                
                {/* Hidden file input */}
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".csv,text/csv"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="csv-file-input"
                />
                
                {/* Drag and drop zone */}
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`
                    border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all
                    ${isDragging 
                      ? 'border-amber-500 bg-amber-50' 
                      : fileName 
                        ? 'border-green-400 bg-green-50' 
                        : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50'
                    }
                  `}
                  data-testid="csv-dropzone"
                >
                  {fileName ? (
                    <div className="flex items-center justify-center gap-3">
                      <FileSpreadsheet className="h-8 w-8 text-green-600" />
                      <div className="text-left">
                        <p className="font-medium text-green-700">{fileName}</p>
                        <p className="text-sm text-green-600">
                          {parseCSV(form.csvData).length} clients found
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          clearFile();
                        }}
                        className="ml-2 text-slate-500 hover:text-red-500"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <FileUp className={`h-10 w-10 mx-auto mb-3 ${isDragging ? 'text-amber-500' : 'text-slate-400'}`} />
                      <p className="font-medium text-slate-700">
                        {isDragging ? 'Drop your CSV file here' : 'Drag & drop your CSV file here'}
                      </p>
                      <p className="text-sm text-slate-500 mt-1">
                        or click to browse
                      </p>
                    </>
                  )}
                </div>

                {/* Show preview of data if loaded */}
                {form.csvData && (
                  <div className="mt-3">
                    <Label className="text-xs text-slate-500">Preview (first 5 rows)</Label>
                    <pre className="mt-1 text-xs text-slate-600 bg-slate-50 p-3 rounded border border-slate-200 overflow-x-auto max-h-32 overflow-y-auto font-mono">
                      {form.csvData.split('\n').slice(0, 6).join('\n')}
                    </pre>
                  </div>
                )}
                
                <p className="text-xs text-slate-500">
                  CSV must have "name" and "email" columns. Download from Pre-Orders → "Download for Bulk Galleries"
                </p>
              </div>

              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-medium text-slate-900 mb-2 flex items-center gap-2">
                  <FileSpreadsheet className="h-4 w-4" />
                  Sample CSV Format
                </h4>
                <pre className="text-xs text-slate-600 bg-white p-3 rounded border border-slate-200 overflow-x-auto">
                  {sampleCSV}
                </pre>
              </div>

              <div className="flex justify-end gap-4 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => navigate('/admin/galleries')}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={loading}
                  data-testid="import-btn"
                >
                  {loading ? (
                    'Importing...'
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Import Galleries
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2 text-green-700">
              <Check className="h-6 w-6" />
              Import Complete
            </CardTitle>
            <CardDescription>
              Successfully created {result.galleries.length} galleries
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 max-h-96 overflow-auto">
              {result.galleries.map((gallery) => (
                <div 
                  key={gallery.id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-slate-900">{gallery.name}</p>
                    <p className="text-sm text-slate-500">{gallery.client_email}</p>
                  </div>
                  <code className="text-sm bg-white px-2 py-1 rounded border border-slate-200 font-mono">
                    {gallery.access_code}
                  </code>
                </div>
              ))}
            </div>
            <div className="flex gap-4 mt-6">
              <Button variant="outline" onClick={() => setResult(null)}>
                Import More
              </Button>
              <Button 
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => navigate('/admin/galleries')}
              >
                View All Galleries
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BulkImportPage;
